/*
 * <BeAlert> -- React wrapper for the .be-alert CSS component (the BeOS modal
 * alert panel: BAlert / TAlertView).
 *
 * Ported from Dano Alert.cpp; the 4 icons are authentic R5 (app_server 'ICON'
 * resources). Dano UI != R5 UI, so the layout still wants an R5 pixel pass --
 * see Notes/06 and components/alert.css.
 *
 * The icons are imported directly: Bun's bundler (dev server + Bun.build) and
 * any consumer bundler resolve the PNG import to a URL automatically (see
 * src/assets.d.ts for the type shim). No base64 generator needed.
 *
 *   <BeAlert
 *     type="stop"
 *     text="Bad media. Do you want to initialize it?"
 *     buttons={["Cancel", "Initialize"]}
 *     onButton={(i) => ...}
 *   />
 *
 * `buttons` are ordered left->right; the rightmost is the default (rendered with
 * the BeOS default-button ring), matching BAlert (SetDefaultButton(lastBut)).
 */

import type { ReactNode } from "react";
import ideaIcon from "../assets/alert/idea.png";
import infoIcon from "../assets/alert/info.png";
import stopIcon from "../assets/alert/stop.png";
import warnIcon from "../assets/alert/warn.png";
import { BeButton } from "./Button";
import { type BeProps, beComponent } from "./create.tsx";

/** Mirrors BAlert's alert_type. "empty" draws no icon (B_EMPTY_ALERT). */
export type BeAlertType = "empty" | "info" | "idea" | "warning" | "stop";

const ALERT_ICONS: Record<Exclude<BeAlertType, "empty">, string> = {
	info: infoIcon,
	idea: ideaIcon,
	warning: warnIcon,
	stop: stopIcon,
};

export type BeAlertProps = BeProps<
	"div",
	{
		/** Which system icon to show. Defaults to "info". */
		type?: BeAlertType;

		/** The message text (word-wrapped, like BAlert's read-only BTextView). */
		text?: ReactNode;

		/**
		 * Button labels, left->right. The rightmost is the default button (drawn
		 * with the default ring), matching BAlert. Defaults to a single "OK".
		 *
		 * Pass `[]` for a button-less, passive alert (icon + text only) used like a
		 * web notice/banner. NOTE: a real R5 BAlert always has >=1 button (the kit
		 * `debugger()`s otherwise) -- the button-less form is a deliberate non-R5
		 * affordance for web use, not faithful BeOS behaviour.
		 */
		buttons?: string[];

		/** Invoked with the index (0-based, left->right) of the clicked button. */
		onButton?: (index: number) => void;

		/** Disable a button by index. */
		disabledButtons?: number[];

		/** Override the panel width (CSS length). Defaults to 310 * --be-px. */
		width?: string;
	}
>;

export const BeAlert = beComponent<BeAlertProps, HTMLDivElement>("BeAlert", (props, be) => {
	const { type = "info", text, buttons = ["OK"], onButton, disabledButtons, scale, width, className, style, ...rest } = props;

	const isEmpty = type === "empty";
	const hasButtons = buttons.length > 0;
	const root = be("be-alert", isEmpty && "is-empty", !hasButtons && "is-buttonless");
	const rootStyle: React.CSSProperties = { ...root.style };
	if (width !== undefined) rootStyle.width = width;

	const lastIndex = buttons.length - 1;

	return (
		<div
			// A passive (button-less) alert is a status message, not a dialog. The
			// message text is the element's content, so it's the accessible name --
			// no aria-label (which biome rejects on a dynamic role anyway).
			role={hasButtons ? "alertdialog" : "alert"}
			{...rest}
			{...root}
			style={Object.keys(rootStyle).length ? rootStyle : undefined}
		>
			<div className="be-alert__content">
				{!isEmpty && <div className="be-alert__strip" />}
				{!isEmpty && <img className="be-alert__icon" src={ALERT_ICONS[type]} alt={type} />}
				<div className="be-alert__body">
					{/* deliberately NOT .be-pixel-text: that class forces display:inline-block so
					    the no-JS promotion has an atomic box, and an inline-block element does
					    not wrap. Alert body copy is multi-line, so it keeps normal block flow
					    and forgoes the pixel treatment (pixel-perfect.css documents this). */}
					<p className="be-alert__text">{text}</p>
					{hasButtons && (
						<div className="be-alert__buttons">
							{buttons.map((label, i) => {
								return (
									<BeButton
										// biome-ignore lint/suspicious/noArrayIndexKey: button order is the identity
										key={i}
										variant={i === lastIndex ? "default" : "normal"}
										disabled={disabledButtons?.includes(i)}
										onClick={() => onButton?.(i)}
									>
										{label}
									</BeButton>
								);
							})}
						</div>
					)}
				</div>
			</div>
		</div>
	);
});
